import { Checkbox, Input, Label, Select, ValidationMessage } from '@eppendorf/vnls-react-components';
import { type FunctionComponentElement, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { OrganizationFormProfile } from './organization-form-profile';

interface Props {
  onSubmit: (formValues: OrganizationFormProfile) => void;
  types: string[];
  subscriptions: string[];
  additionalFeatures?: string[];
  tenantFieldsDisabled?: boolean;
}

export const OrganizationFormId = 'organizationForm';

export function OrganizationForm({
  onSubmit,
  types,
  subscriptions,
  additionalFeatures,
  tenantFieldsDisabled = false,
}: Props): FunctionComponentElement<Props> {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors: formErrors, isSubmitting },
  } = useFormContext<OrganizationFormProfile>();
  const subscriptionModels = useMemo(
    () =>
      subscriptions.map((subscription) => ({
        label: t(`addOrganization.subscriptions.${subscription}`),
        value: subscription,
      })),
    [subscriptions, t],
  );

  function handleFormSubmit(form: OrganizationFormProfile): void {
    onSubmit({ ...form });
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} id={OrganizationFormId}>
      <div className="flex__dir--row w-full">
        <div className="w-full p-x-m">
          <div className="m-bottom-m">
            <Label htmlFor="name">{t('addOrganization.name')}*</Label>
            <Input
              invalid={!!formErrors.name}
              id="name"
              type="text"
              className="input m-top-xs m-bottom-xs"
              // eslint-disable-next-line react/jsx-props-no-spreading -- spread is needed for react-hook-form
              {...register('name', {
                disabled: tenantFieldsDisabled,
                required: !tenantFieldsDisabled,
              })}
              disabled={isSubmitting || tenantFieldsDisabled}
            />
            {formErrors.name?.type === 'required' && <ValidationMessage>{t('validationMessages.required')}</ValidationMessage>}
          </div>

          <div className="m-bottom-m">
            <Label htmlFor="type">{t('addOrganization.type')}*</Label>
            <Controller
              name="type"
              control={control}
              rules={{ required: !tenantFieldsDisabled }}
              render={({ field }) => (
                <Select
                  options={types.map((name) => ({ label: name, value: name }))}
                  className="input m-top-xs m-bottom-xs"
                  disabled={isSubmitting || tenantFieldsDisabled}
                  // eslint-disable-next-line react/jsx-props-no-spreading -- spread is needed for react-hook-form
                  {...field}
                />
              )}
            />
            {formErrors.subscription?.type === 'required' && <ValidationMessage>{t('validationMessages.required')}</ValidationMessage>}
          </div>

          <div className="m-bottom-m">
            <Label htmlFor="subscription">{t('addOrganization.subscription')}*</Label>
            <Controller
              name="subscription"
              control={control}
              rules={{ required: !tenantFieldsDisabled }}
              render={({ field }) => (
                <Select
                  options={subscriptionModels}
                  className="input m-top-xs m-bottom-xs"
                  disabled={isSubmitting || tenantFieldsDisabled}
                  // eslint-disable-next-line react/jsx-props-no-spreading -- spread is needed for react-hook-form
                  {...field}
                />
              )}
            />
            {formErrors.subscription?.type === 'required' && <ValidationMessage>{t('validationMessages.required')}</ValidationMessage>}
          </div>

          <div className="m-bottom-m">
            <Label htmlFor="subscribedDevices">{t('addOrganization.subscribedDevices')}*</Label>
            <Input
              id="subscribedDevices"
              type="text"
              className="input m-top-xs m-bottom-xs"
              // eslint-disable-next-line react/jsx-props-no-spreading -- spread is needed for react-hook-form
              {...register('subscribedDevices', {
                disabled: tenantFieldsDisabled,
                required: !tenantFieldsDisabled,
                pattern: /^\d+$/,
              })}
              disabled={isSubmitting || tenantFieldsDisabled}
            />
            {formErrors.subscribedDevices?.type === 'required' && <ValidationMessage>{t('validationMessages.required')}</ValidationMessage>}
            {formErrors.subscribedDevices?.type === 'pattern' && (
              <ValidationMessage>{t('addOrganization.errorMessageNumberWrongFormat')}</ValidationMessage>
            )}
          </div>

          {additionalFeatures?.map((feature) => (
            <div className="m-bottom-m" key={feature}>
              <Controller
                name="additionalFeatures"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    id={`additional-feature-${feature}`}
                    checked={field.value?.includes(feature) ?? false}
                    disabled={isSubmitting}
                    onBlur={field.onBlur}
                    onChange={(checked) => {
                      const otherFeatures = field.value?.filter((value) => value !== feature) ?? [];
                      field.onChange(checked === true ? [...otherFeatures, feature] : otherFeatures);
                    }}
                    ariaLabel={feature === 'REPORT_HUB_ACCESS' ? 'Report Hub access' : feature}
                  />
                )}
              />
              <Label htmlFor={`additional-feature-${feature}`} className="m-left-xs">
                {feature === 'REPORT_HUB_ACCESS' ? 'Report Hub access' : feature}
              </Label>
            </div>
          ))}
        </div>
        <div className="w-full p-x-m">
          <div className="m-bottom-m">
            <Label htmlFor="contactFirstName">{t('addOrganization.contactFirstName')}*</Label>
            <Input
              invalid={!!formErrors.contactFirstName}
              id="contactFirstName"
              type="text"
              className="input m-top-xs m-bottom-xs"
              // eslint-disable-next-line react/jsx-props-no-spreading -- spread is needed for react-hook-form
              {...register('contactFirstName', {
                disabled: tenantFieldsDisabled,
                required: !tenantFieldsDisabled,
              })}
              disabled={isSubmitting || tenantFieldsDisabled}
            />
            {formErrors.contactFirstName?.type === 'required' && <ValidationMessage>{t('validationMessages.required')}</ValidationMessage>}
          </div>

          <div className="m-bottom-m">
            <Label htmlFor="contactLastName">{t('addOrganization.contactLastName')}*</Label>
            <Input
              invalid={!!formErrors.contactLastName}
              id="contactLastName"
              type="text"
              className="input m-top-xs m-bottom-xs"
              // eslint-disable-next-line react/jsx-props-no-spreading -- spread is needed for react-hook-form
              {...register('contactLastName', {
                disabled: tenantFieldsDisabled,
                required: !tenantFieldsDisabled,
              })}
              disabled={isSubmitting || tenantFieldsDisabled}
            />
            {formErrors.contactLastName?.type === 'required' && <ValidationMessage>{t('validationMessages.required')}</ValidationMessage>}
          </div>

          <div className="m-bottom-m">
            <Label htmlFor="contactEmail">{t('addOrganization.contactEmail')}*</Label>
            <Input
              invalid={!!formErrors.contactEmail}
              id="contactEmail"
              type="text"
              className="input m-top-xs m-bottom-xs"
              // eslint-disable-next-line react/jsx-props-no-spreading -- spread is needed for react-hook-form
              {...register('contactEmail', {
                disabled: tenantFieldsDisabled,
                required: !tenantFieldsDisabled,
                maxLength: 120,
                // eslint-disable-next-line regexp/no-super-linear-backtracking -- the implementation used seems to be fine and anyway it would only slow down the user's browser
                pattern: /^\S+@\S+\.\S{2,}$/,
                setValueAs: (v) => v.trim().toLowerCase(),
              })}
              disabled={isSubmitting || tenantFieldsDisabled}
            />
            {formErrors.contactEmail?.type === 'required' && <ValidationMessage>{t('validationMessages.required')}</ValidationMessage>}
            {formErrors.contactEmail?.type === 'maxLength' && (
              <ValidationMessage>{t('addOrganization.errorMessageContactEmailMaxLength')}</ValidationMessage>
            )}
            {formErrors.contactEmail?.type === 'pattern' && (
              <ValidationMessage>{t('addOrganization.errorMessageContactEmailWrongFormat')}</ValidationMessage>
            )}
          </div>

          <div className="m-bottom-m">
            <Label htmlFor="adminFirstName">{t('addOrganization.adminFirstName')}*</Label>
            <Input
              invalid={!!formErrors.adminFirstName}
              id="adminFirstName"
              type="text"
              className="input m-top-xs m-bottom-xs"
              // eslint-disable-next-line react/jsx-props-no-spreading -- spread is needed for react-hook-form
              {...register('adminFirstName', {
                disabled: tenantFieldsDisabled,
                required: !tenantFieldsDisabled,
              })}
              disabled={isSubmitting || tenantFieldsDisabled}
            />
            {formErrors.adminFirstName?.type === 'required' && <ValidationMessage>{t('validationMessages.required')}</ValidationMessage>}
          </div>

          <div className="m-bottom-m">
            <Label htmlFor="adminLastName">{t('addOrganization.adminLastName')}*</Label>
            <Input
              invalid={!!formErrors.adminLastName}
              id="adminLastName"
              type="text"
              className="input m-top-xs m-bottom-xs"
              // eslint-disable-next-line react/jsx-props-no-spreading -- spread is needed for react-hook-form
              {...register('adminLastName', {
                disabled: tenantFieldsDisabled,
                required: !tenantFieldsDisabled,
              })}
              disabled={isSubmitting || tenantFieldsDisabled}
            />
            {formErrors.adminLastName?.type === 'required' && <ValidationMessage>{t('validationMessages.required')}</ValidationMessage>}
          </div>

          <div className="m-bottom-m">
            <Label htmlFor="adminEmail">{t('addOrganization.adminEmail')}*</Label>
            <Input
              invalid={!!formErrors.adminEmail}
              id="adminEmail"
              type="text"
              className="input m-top-xs m-bottom-xs"
              // eslint-disable-next-line react/jsx-props-no-spreading -- spread is needed for react-hook-form
              {...register('adminEmail', {
                disabled: tenantFieldsDisabled,
                required: !tenantFieldsDisabled,
                maxLength: 120,
                // eslint-disable-next-line regexp/no-super-linear-backtracking -- the implementation used seems to be fine and anyway it would only slow down the user's browser
                pattern: /^\S+@\S+\.\S{2,}$/,
                setValueAs: (v) => v.trim().toLowerCase(),
              })}
              disabled={isSubmitting || tenantFieldsDisabled}
            />
            {formErrors.adminEmail?.type === 'required' && <ValidationMessage>{t('validationMessages.required')}</ValidationMessage>}
            {formErrors.adminEmail?.type === 'conflict' && <ValidationMessage>{formErrors.adminEmail?.message}</ValidationMessage>}
            {formErrors.adminEmail?.type === 'maxLength' && (
              <ValidationMessage>{t('addOrganization.errorMessageAdminEmailMaxLength')}</ValidationMessage>
            )}
            {formErrors.adminEmail?.type === 'pattern' && (
              <ValidationMessage>{t('addOrganization.errorMessageAdminEmailWrongFormat')}</ValidationMessage>
            )}
          </div>
        </div>
      </div>
    </form>
  );
}
